import React from "react";

import { useTurnstile, Turnstile } from "react-turnstile";

import { type VerificationService } from "@/lib/types/types";

import { useFlag } from "@/components/FeatureFlags/hooks";
import { MetricIds, useMetrics } from "@/components/FeatureFlags/useMetrics";

import { getQueryOrOption } from "@/lib/ServerApi/TestingRouteOverrides";

import { QUERY_STRING_CAPTCHA_ERROR_OVERRIDE } from "@/constants";

import { CaptchaError } from "@/lib/types/runtimeTypes";

import { setViewModel } from "@/lib/VerificationService/ViewModel";
import { updateFieldValidationErrorsByFieldId } from "@/lib/utils/stepComponentHelpers/stepComponentHelpers";

import { isCaptchaEnabled } from "../../../lib/ProgramTheme/programThemeGetters";

import { CaptchaErrorComponent, type CaptchaErrorType } from "./CaptchaErrorComponent";

export interface CaptchaComponentProps {
  verificationService: VerificationService;
  isErrored: boolean;
}

const CAPTCHA_RETRY_INTERVAL = 120000; // 2 minutes
const CAPTCHA_LOAD_TIMEOUT = 10000; // 10 seconds

const captchaErrorMetricMap = {
  [CaptchaError.SERVER]: MetricIds.CaptchaErrorServer,
  [CaptchaError.BROWSER_COMPATIBILITY]: MetricIds.CaptchaErrorBrowserCompatibility,
} as const;

/**
 * CloudFlare Turnstile invisible captcha. Conditionally loads the captcha based on program theme and renders any related errors.
 */
export const CaptchaComponent = ({ verificationService, isErrored }: CaptchaComponentProps) => {
  const turnstile = useTurnstile();
  const captchaEnabledFlag = useFlag("captcha") === "enabled";

  const { programTheme, programId } = verificationService;
  const segment = verificationService.verificationResponse.segment;
  const captchaEnabled = isCaptchaEnabled(programTheme) || captchaEnabledFlag;

  const { trackEvent, recordNumericMetric, flush } = useMetrics({ segment, programId });
  const firedMetrics = React.useRef(new Set<string>());
  const trackOnce = (metricId: (typeof MetricIds)[keyof typeof MetricIds]) => {
    if (!firedMetrics.current.has(metricId)) {
      firedMetrics.current.add(metricId);
      trackEvent(metricId);
    }
  };

  const mockCaptchaError = getQueryOrOption(QUERY_STRING_CAPTCHA_ERROR_OVERRIDE);
  const mockError = Object.values(CaptchaError).includes(mockCaptchaError as CaptchaError)
    ? (mockCaptchaError as CaptchaError)
    : null;

  const [error, setError] = React.useState<CaptchaErrorType>(mockError);
  const [hasLoaded, setHasLoaded] = React.useState(false);

  const handleRetry = () => {
    trackOnce(MetricIds.CaptchaRetryClicked);
    setError(null);
    turnstile.reset();
  };

  const updateCaptchaToken = (value: string) => {
    setViewModel({ captchaToken: value });
    updateFieldValidationErrorsByFieldId("captchaToken", value, verificationService);
  };

  const handleError = (errorType: CaptchaError) => {
    trackOnce(captchaErrorMetricMap[errorType]);
    setError(errorType);
    updateCaptchaToken("");
  };

  const getFieldsToSkip = () => verificationService.viewModel?.fieldsToSkipValidation || [];

  const registerCaptchaField = () => {
    const fieldsToSkip = getFieldsToSkip();
    setViewModel({
      fieldsToSkipValidation: fieldsToSkip.filter((id) => id !== "captchaToken"),
    });
    updateCaptchaToken("");
  };

  const unregisterCaptchaField = () => {
    const fieldsToSkip = getFieldsToSkip();
    setViewModel({
      captchaToken: undefined,
      fieldsToSkipValidation: fieldsToSkip.includes("captchaToken")
        ? fieldsToSkip
        : [...fieldsToSkip, "captchaToken"],
    });
  };

  // Show server error when captcha token is rejected after submission,
  // but don't overwrite an existing client-side error.
  React.useEffect(() => {
    if (captchaEnabled && isErrored) {
      setError((prev) => {
        if (prev === null) {
          trackOnce(MetricIds.CaptchaErrorServer);
          return CaptchaError.SERVER;
        }
        return prev;
      });
    }
  }, [captchaEnabled, isErrored]);

  React.useEffect(() => {
    if (!captchaEnabled) {
      return;
    }
    registerCaptchaField();

    const hasTerminalMetric = () => {
      const metrics = firedMetrics.current;
      return (
        metrics.has(MetricIds.CaptchaVerified) ||
        metrics.has(MetricIds.CaptchaErrorClient) ||
        metrics.has(MetricIds.CaptchaErrorScriptLoad) ||
        metrics.has(MetricIds.CaptchaErrorBrowserCompatibility) ||
        metrics.has(MetricIds.CaptchaErrorLoadTimeout)
      );
    };

    const trackUnaccounted = () => {
      if (hasTerminalMetric()) {
        return;
      }
      if (!firedMetrics.current.has(MetricIds.CaptchaRendered)) {
        trackOnce(MetricIds.CaptchaUnmountedBeforeRender);
      } else {
        trackOnce(MetricIds.CaptchaAbandonedAfterRender);
      }
    };

    const onBeforeUnload = () => {
      trackUnaccounted();
      flush();
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      trackUnaccounted();
      unregisterCaptchaField();
    };
  }, [captchaEnabled]);

  React.useEffect(() => {
    if (!captchaEnabled || hasLoaded) {
      return;
    }
    const timer = setTimeout(() => {
      if (firedMetrics.current.has(MetricIds.CaptchaErrorScriptLoad)) {
        return;
      }
      trackOnce(MetricIds.CaptchaErrorLoadTimeout);
      setError(CaptchaError.CLIENT);
      updateCaptchaToken("");
    }, CAPTCHA_LOAD_TIMEOUT);

    return () => clearTimeout(timer);
  }, [captchaEnabled, hasLoaded]);

  if (!captchaEnabled) {
    return null;
  }

  return (
    <div>
      <Turnstile
        sitekey={import.meta.env.VITE_CAPTCHA_SITE_KEY}
        refreshExpired="auto"
        retryInterval={CAPTCHA_RETRY_INTERVAL}
        onLoad={() => {
          setHasLoaded(true);
          trackOnce(MetricIds.CaptchaRendered);
        }}
        onVerify={(token) => {
          trackOnce(MetricIds.CaptchaVerified);
          // Clear client-side errors on successful verify, but preserve
          // server errors so the user knows to resubmit.
          setError((prev) => (prev === CaptchaError.SERVER ? prev : null));
          updateCaptchaToken(token);
        }}
        onError={(error) => {
          if (!hasLoaded) {
            trackOnce(MetricIds.CaptchaErrorScriptLoad);
          } else if (!firedMetrics.current.has(MetricIds.CaptchaErrorClient)) {
            const errorCode = parseInt(error, 10);
            recordNumericMetric(MetricIds.CaptchaErrorClientCode, isNaN(errorCode) ? 0 : errorCode);
            trackOnce(MetricIds.CaptchaErrorClient);
          }
          setError(CaptchaError.CLIENT);
          updateCaptchaToken("");
        }}
        onUnsupported={() => {
          handleError(CaptchaError.BROWSER_COMPATIBILITY);
        }}
      />

      <CaptchaErrorComponent
        errorType={error}
        onRetry={hasLoaded && error === CaptchaError.CLIENT ? handleRetry : undefined}
      />
    </div>
  );
};
